import type { Plan, FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import { BASIC } from '../../revenue/plan/feature-group';

import type { AnalyticsProduct, FeedbackProduct, Product, ProductCatalog } from './types';
import { ProductType } from './types';

export const getProductSkusByPlanId = (planId: string): string[] => {
  return planId.split(';');
};

export const getBundledPlanId = ({
  analyticsPlanId,
  feedbackPlanId,
}: {
  analyticsPlanId: string;
  feedbackPlanId: string;
}): string => {
  return `${analyticsPlanId};${feedbackPlanId}`;
};

/**
 * @param planId Bundled plan ID, for example: `analytics_basic_2000;feedback_business_500`
 * @param type Which product's SKU to get from the bundled plan ID
 * @returns SKU, for example: `feedback_business_500`
 */
export function extractSkuFromPlanId(planId: string, type: ProductType): string {
  const [analytics, feedback] = getProductSkusByPlanId(planId);

  const skus = {
    [ProductType.Analytics]: analytics,
    [ProductType.Feedback]: feedback,
  };

  return skus[type];
}

export function getAnalyticsProductBySku(
  analyticsSku: string,
  productCatalog: ProductCatalog,
): AnalyticsProduct {
  return (
    productCatalog.analyticsProducts.find((product) => product.sku === analyticsSku) ??
    productCatalog.analyticsProducts[0]
  );
}

export function getFeedbackProductBySku(
  feedbackSku: string,
  productCatalog: ProductCatalog,
): FeedbackProduct {
  return (
    productCatalog.feedbackProducts.find((product) => product.sku === feedbackSku) ??
    productCatalog.feedbackProducts[0]
  );
}

/**
 * Finds and returns a product by SKU from the product catalog, without caring about if it's an Ask or Observe
 * product.
 *
 * @param sku SKU, for example: `feedback_business_500`
 * @param productCatalog The product catalog object
 * @returns Product from the product catalog with a matching SKU
 */
export function getProductBySku(
  sku: string,
  productCatalog: ProductCatalog | undefined,
): Product | null {
  if (!productCatalog) {
    return null;
  }

  const products: Product[] = Object.values(productCatalog).flat();

  return products.find((product) => product.sku === sku) ?? null;
}

const sortByType = (itemA: Product, itemB: Product): number => itemA.type.localeCompare(itemB.type);

/**
 * Finds and returns all free tier products (Current assumption is that basic tier products will always be free)
 * Sorts the result by product type name.
 *
 * @param productCatalog The product catalog object
 */
export function getFreeTierProducts(productCatalog: ProductCatalog | undefined): Product[] | null {
  if (!productCatalog) {
    return null;
  }
  const products: Product[] = Object.values(productCatalog).flat();

  return products.filter((product) => product.tier === BASIC).sort(sortByType);
}

/**
 * The product catalog is not indexed with values of the `ProductType` enum, so we need a function like this to access
 * the product catalog's Ask or Observe slice in a type-safe and reusable way.
 *
 * @param productType Type of products that you want to get from the product catalog
 * @param productCatalog The product catalog object
 * @returns Array of products from the product catalog filtered to a certain product type (Ask or Observe)
 */
export function getProductsByProductType(
  productType: ProductType | undefined,
  productCatalog: ProductCatalog | undefined,
): Array<Product> {
  if (!productType || !productCatalog) {
    return [];
  }

  switch (productType) {
    case ProductType.Analytics:
      return productCatalog.analyticsProducts;
    case ProductType.Feedback:
      return productCatalog.feedbackProducts;
    default:
      throw new Error(`getProductsByProductType(): Unknown product type ${productType}.`);
  }
}

export function assertIsFeedbackProduct(
  maybeFeedback: Product | null,
): asserts maybeFeedback is FeedbackProduct {
  if (!maybeFeedback) throw new Error('Expected Feedback product but found null.');
  if (!(maybeFeedback.type === ProductType.Feedback))
    throw new Error(`Expected Feedback product but found unknown type. ${maybeFeedback.type}`);
}

export function isProduct(
  maybeIsProduct: Plan | AnalyticsProduct | FeedbackProduct | null,
): maybeIsProduct is AnalyticsProduct | FeedbackProduct {
  return !!(maybeIsProduct && 'sku' in maybeIsProduct);
}

export function isBundledPlanId(planId: string): boolean {
  return planId.includes(';');
}

export function getDefaultProductByTier<T extends ProductType>(
  tier: FeatureGroup,
  productType: T,
  productCatalog: ProductCatalog | undefined,
): Product | null {
  if (!productCatalog) {
    return null;
  }

  const products = getProductsByProductType(productType, productCatalog);

  return products.find((product) => product.tier === tier) ?? null;
}

export function getProductAllowance(product: Product | null | undefined): number {
  if (!product) {
    return 0;
  }

  switch (product.type) {
    case ProductType.Analytics:
      return product.dailyCcSessions;
    case ProductType.Feedback:
      return product.monthlyResponses;
    default:
      throw new Error('getProductAllowance(): Could not extract allowance from product object.');
  }
}
